import React, { createContext, useContext, useState, ReactNode } from 'react';
import { aiService, Message, ChatResponse, PlaylistData } from '../services';

// Only log in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Debug logger function
const debugLog = (message: string, data?: any) => {
  if (isDevelopment) {
    console.log(`[AIContext] ${message}`);
    if (data) console.log(data);
  }
};

export interface AIContextType {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<ChatResponse>;
  getMusicRecommendations: (preferences: {
    genres?: string[];
    artists?: string[];
    mood?: string;
    activity?: string;
    description?: string;
  }) => Promise<ChatResponse>;
  generatePlaylist: (
    description: string,
    options?: {
      favoriteTracks?: string[];
      favoriteArtists?: string[];
      mood?: string;
      activity?: string;
      era?: string;
      tempo?: string;
      genreDiversity?: 'low' | 'medium' | 'high';
      includeObscure?: boolean;
      trackCount?: number;
    }
  ) => Promise<ChatResponse>;
  modifyPlaylist: (
    playlist: PlaylistData,
    instructions: string,
    options?: {
      addTracks?: number;
      removeTracks?: number;
      keepPercentage?: number;
    }
  ) => Promise<ChatResponse>;
  analyzePlaylist: (
    playlist: PlaylistData,
    analysisType?: 'general' | 'genre' | 'mood' | 'era' | 'all'
  ) => Promise<ChatResponse>;
  mergePlaylists: (
    playlist1: PlaylistData,
    playlist2: PlaylistData,
    instructions?: string
  ) => Promise<ChatResponse>;
  transformPlaylist: (
    playlist: PlaylistData,
    transformation: string
  ) => Promise<ChatResponse>;
  extractPlaylistFromResponse: (response: string) => PlaylistData | null;
  cleanResponseText: (response: string) => string;
  resetConversation: () => void;
}

const AIContext = createContext<AIContextType | undefined>(undefined);

interface AIProviderProps {
  children: ReactNode;
}

export const AIProvider: React.FC<AIProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = async (message: string): Promise<ChatResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiService.sendMessage(message);
      
      // Update messages state with the new message and response
      setMessages(aiService.getConversationHistory());
      
      return response;
    } catch (err: any) {
      // More specific error messages based on error type
      if (err.response) {
        if (err.response.status === 429) {
          setError('Rate limit exceeded. Please try again in a moment.');
        } else if (err.response.status === 401 || err.response.status === 403) {
          setError('Authentication error. Please try logging in again.');
        } else {
          setError(`Server error: ${err.response.status}. Please try again later.`);
        }
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      debugLog('Error sending message:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getMusicRecommendations = async (preferences: {
    genres?: string[];
    artists?: string[];
    mood?: string;
    activity?: string;
    description?: string;
  }): Promise<ChatResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiService.getMusicRecommendations(preferences);
      
      // Update messages state
      setMessages(aiService.getConversationHistory());
      
      return response;
    } catch (err: any) {
      // More specific error messages based on error type
      if (err.response) {
        if (err.response.status === 429) {
          setError('Rate limit exceeded. Please try again in a moment.');
        } else if (err.response.status === 401 || err.response.status === 403) {
          setError('Authentication error. Please try logging in again.');
        } else {
          setError(`Server error: ${err.response.status}. Please try again later.`);
        }
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to get music recommendations. Please try again.');
      }
      debugLog('Error getting music recommendations:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const generatePlaylist = async (
    description: string,
    options?: {
      favoriteTracks?: string[];
      favoriteArtists?: string[];
      mood?: string;
      activity?: string;
      era?: string;
      tempo?: string;
      genreDiversity?: 'low' | 'medium' | 'high';
      includeObscure?: boolean;
      trackCount?: number;
    }
  ): Promise<ChatResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiService.generatePlaylist(description, options);
      
      // Update messages state
      setMessages(aiService.getConversationHistory());
      
      return response;
    } catch (err) {
      setError('Failed to generate playlist');
      debugLog('Error generating playlist:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const modifyPlaylist = async (
    playlist: PlaylistData,
    instructions: string,
    options?: {
      addTracks?: number;
      removeTracks?: number;
      keepPercentage?: number;
    }
  ): Promise<ChatResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiService.modifyPlaylist(playlist, instructions, options);
      
      // Update messages state
      setMessages(aiService.getConversationHistory());
      
      return response;
    } catch (err) {
      setError('Failed to modify playlist');
      debugLog('Error modifying playlist:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const analyzePlaylist = async (
    playlist: PlaylistData,
    analysisType: 'general' | 'genre' | 'mood' | 'era' | 'all' = 'all'
  ): Promise<ChatResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiService.analyzePlaylist(playlist, analysisType);
      
      // Update messages state
      setMessages(aiService.getConversationHistory());
      
      return response;
    } catch (err) {
      setError('Failed to analyze playlist');
      debugLog('Error analyzing playlist:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const mergePlaylists = async (
    playlist1: PlaylistData,
    playlist2: PlaylistData,
    instructions?: string
  ): Promise<ChatResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiService.mergePlaylists(playlist1, playlist2, instructions);
      
      // Update messages state
      setMessages(aiService.getConversationHistory());
      
      return response;
    } catch (err) {
      setError('Failed to merge playlists');
      debugLog('Error merging playlists:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const transformPlaylist = async (
    playlist: PlaylistData,
    transformation: string
  ): Promise<ChatResponse> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await aiService.transformPlaylist(playlist, transformation);
      
      // Update messages state
      setMessages(aiService.getConversationHistory());
      
      return response;
    } catch (err) {
      setError('Failed to transform playlist');
      debugLog('Error transforming playlist:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const extractPlaylistFromResponse = (response: string): PlaylistData | null => {
    return aiService.extractPlaylistFromResponse(response);
  };
  
  const cleanResponseText = (response: string): string => {
    return aiService.cleanResponseText(response);
  };

  const resetConversation = (): void => {
    aiService.resetConversation();
    setMessages([]);
    setError(null);
  };

  const value = {
    messages,
    isLoading,
    error,
    sendMessage,
    getMusicRecommendations,
    generatePlaylist,
    modifyPlaylist,
    analyzePlaylist,
    mergePlaylists,
    transformPlaylist,
    extractPlaylistFromResponse,
    cleanResponseText,
    resetConversation,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};

export const useAI = (): AIContextType => {
  const context = useContext(AIContext);
  
  if (context === undefined) {
    throw new Error('useAI must be used within an AIProvider');
  }
  
  return context;
};
